import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useTheme } from "../../../contexts/ThemeContext";
import { useAuth } from "../../../contexts/AuthContext";
import { temNivelMinimo } from "../../../utils/permissoes";
import { NIVEIS } from "../../../config/niveis";
import LayoutDesktop from "../../../layouts/LayoutDesktop";
import LayoutMobile from "../../../layouts/LayoutMobile";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { db } from "../../../config/firebase";
import { doc, getDoc, serverTimestamp, updateDoc } from "firebase/firestore";
import { uploadToStorage } from "../../../utils/storageUpload";

function criarIdItemChecklist() {
  try {
    if (globalThis?.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  } catch (e) {}
  return `item_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

const TIPO_CHECKLIST_OPCOES = [
  { value: "INSTALACAO", label: "Instalacao" },
  { value: "OS", label: "OS" },
];

function EditarChecklist() {
  const { tema, alterarTema } = useTheme();
  const { usuario: usuarioLogado } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const isMobile = useIsMobile(900);

  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");

  const [nome, setNome] = useState("");
  const [fabricante, setFabricante] = useState("");
  const [modelo, setModelo] = useState("");
  const [tipo, setTipo] = useState("INSTALACAO");

  const [codigo, setCodigo] = useState("");
  const [empresaId, setEmpresaId] = useState("sem_empresa");
  const [grupoIdChecklist, setGrupoIdChecklist] = useState("");

  const [itens, setItens] = useState([]);
  const inputItemFotoCameraRefs = useRef([]);
  const inputItemFotoArquivoRefs = useRef([]);

  useEffect(() => {
    let ativo = true;

    async function carregarDados() {
      if (!usuarioLogado?.grupoId || !id) {
        setCarregando(false);
        return;
      }

      if (!temNivelMinimo(usuarioLogado, NIVEIS.ADMIN)) {
        setErro("Apenas Administrador/Gestor pode editar checklists.");
        setCarregando(false);
        return;
      }

      setCarregando(true);
      setErro("");
      setSucesso("");

      try {
        const ref = doc(db, "checklistsModelos", id);
        const snap = await getDoc(ref);

        if (!snap.exists()) {
          if (ativo) {
            setErro("Checklist nao encontrado.");
          }
          return;
        }

        const dados = snap.data();

        if (
          dados.grupoId &&
          usuarioLogado.grupoId &&
          dados.grupoId !== usuarioLogado.grupoId
        ) {
          if (ativo) {
            setErro("Voce nao tem permissao para editar este checklist.");
          }
          return;
        }

        if (!ativo) return;

        setGrupoIdChecklist(dados.grupoId || usuarioLogado.grupoId);
        setEmpresaId(dados.empresaId || "sem_empresa");
        setCodigo(dados.codigo || "");
        setNome(dados.nome || "");
        setFabricante(dados.fabricante || "");
        setModelo(dados.modelo || "");
        setTipo(dados.tipo || "INSTALACAO");
        setItens(
          Array.isArray(dados.itens)
            ? dados.itens.map((item) => ({
                id: item?.id || criarIdItemChecklist(),
                pergunta: item?.pergunta || "",
                tipoResposta: item?.tipoResposta || "SIM_NAO",
                periodicidade: item?.periodicidade || undefined,
                fotoUrl: item?.fotoUrl || null,
                fotoFile: null,
                fotoPreview: null,
              }))
            : []
        );
      } catch (err) {
        console.error(err);
        if (ativo) {
          setErro("Erro ao carregar checklist" + (err?.message ? `: ${err.message}` : ""));
        }
      } finally {
        if (ativo) {
          setCarregando(false);
        }
      }
    }

    carregarDados();

    return () => {
      ativo = false;
      inputItemFotoCameraRefs.current = [];
      inputItemFotoArquivoRefs.current = [];
    };
  }, [id, usuarioLogado]);

  const adicionarItem = () => {
    setItens((prev) => [
      ...prev,
      {
        id: criarIdItemChecklist(),
        pergunta: "",
        tipoResposta: "SIM_NAO",
        fotoUrl: null,
        fotoFile: null,
        fotoPreview: null,
      },
    ]);
  };

  const atualizarItem = (index, campo, valor) => {
    setItens((prev) => {
      const novo = [...prev];
      novo[index] = {
        ...novo[index],
        [campo]: valor,
      };
      return novo;
    });
  };

  const removerItem = (index) => {
    setItens((prev) => prev.filter((_, i) => i !== index));
  };

  const atualizarFotoItem = (index, file) => {
    setItens((prev) => {
      const novo = [...prev];
      const atual = { ...(novo[index] || {}) };

      if (atual.fotoPreview) {
        try {
          URL.revokeObjectURL(atual.fotoPreview);
        } catch (e) {}
      }

      if (file) {
        atual.fotoFile = file;
        atual.fotoPreview = URL.createObjectURL(file);
      } else {
        atual.fotoFile = null;
        atual.fotoPreview = null;
        atual.fotoUrl = null;
      }

      novo[index] = atual;
      return novo;
    });
  };

  const removerFotoItem = (index) => {
    setItens((prev) => {
      const novo = [...prev];
      const atual = { ...(novo[index] || {}) };

      if (atual.fotoPreview) {
        try {
          URL.revokeObjectURL(atual.fotoPreview);
        } catch (e) {}
      }

      atual.fotoFile = null;
      atual.fotoPreview = null;
      atual.fotoUrl = null;
      novo[index] = atual;
      return novo;
    });
  };

  const handleSubmit = async (evento) => {
    evento.preventDefault();
    setErro("");
    setSucesso("");

    if (!usuarioLogado?.grupoId) {
      setErro("Usuario logado nao possui grupo definido. Verifique o cadastro do Gestor.");
      return;
    }

    if (!temNivelMinimo(usuarioLogado, NIVEIS.ADMIN)) {
      setErro("Apenas Administrador/Gestor pode editar checklists.");
      return;
    }

    if (!tipo) {
      setErro("Selecione o tipo do checklist.");
      return;
    }

    if (!nome.trim()) {
      setErro("Informe o nome do checklist.");
      return;
    }

    if (!fabricante.trim()) {
      setErro("Informe o fabricante da maquina/modelo.");
      return;
    }

    if (!modelo.trim()) {
      setErro("Informe o modelo da maquina.");
      return;
    }

    try {
      setSalvando(true);
      const grupoId = grupoIdChecklist || usuarioLogado.grupoId;

      const itensProcessados = [];
      for (let i = 0; i < itens.length; i += 1) {
        const item = itens[i] || {};
        let fotoUrl = item.fotoUrl || null;

        if (item.fotoFile) {
          fotoUrl = await uploadToStorage({
            grupoId,
            empresaId: empresaId || "sem_empresa",
            pasta: "checklists/itens",
            file: item.fotoFile,
            filenamePrefix: `${codigo || id || "checklist"}_item${String(i + 1).padStart(2, "0")}`,
          });
        }

        itensProcessados.push({
          id: item.id || criarIdItemChecklist(),
          pergunta: (item.pergunta || "").trim(),
          tipoResposta: item.tipoResposta || "SIM_NAO",
          fotoUrl: fotoUrl || null,
        });
      }

      await updateDoc(doc(db, "checklistsModelos", id), {
        tipo,
        nome: nome.trim(),
        fabricante: fabricante.trim(),
        modelo: modelo.trim(),
        itens: itensProcessados,
        updatedAt: serverTimestamp(),
      });

      setSucesso("Checklist atualizado com sucesso!");
    } catch (err) {
      console.error(err);
      setErro("Erro ao salvar checklist" + (err?.message ? `: ${err.message}` : ""));
    } finally {
      setSalvando(false);
    }
  };

  const renderFormulario = () => (
    <>
      {erro && (
        <div
          style={{
            marginTop: 12,
            padding: 8,
            borderRadius: 8,
            background: "#7f1d1d",
            color: "#fecaca",
            fontSize: 14,
          }}
        >
          {erro}
        </div>
      )}

      {sucesso && (
        <div
          style={{
            marginTop: 12,
            padding: 8,
            borderRadius: 8,
            background: "#14532d",
            color: "#bbf7d0",
            fontSize: 14,
          }}
        >
          {sucesso}
        </div>
      )}

      <form onSubmit={handleSubmit} style={{ marginTop: 16 }}>
        <div className="pc-form-group">
          <label>Tipo *</label>
          <select value={tipo} onChange={(e) => setTipo(e.target.value)} className="pc-input">
            {TIPO_CHECKLIST_OPCOES.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="pc-form-group">
          <label>Nome *</label>
          <input className="pc-input" value={nome} onChange={(e) => setNome(e.target.value)} />
        </div>

        <div className="pc-form-group">
          <label>Fabricante *</label>
          <input className="pc-input" value={fabricante} onChange={(e) => setFabricante(e.target.value)} />
        </div>

        <div className="pc-form-group">
          <label>Modelo *</label>
          <input className="pc-input" value={modelo} onChange={(e) => setModelo(e.target.value)} />
        </div>

        <div style={{ marginTop: 24 }}>
          <h4>Itens do checklist</h4>

          {itens.length === 0 && (
            <div style={{ marginTop: 8, fontSize: 14, opacity: 0.8 }}>
              Nenhum item adicionado ainda.
            </div>
          )}

          {itens.map((item, idx) => (
            <div
              key={item.id || idx}
              style={{
                marginTop: 12,
                padding: 12,
                border: "1px solid #334155",
                borderRadius: 10,
              }}
            >
              <div className="pc-form-group">
                <label>Pergunta *</label>
                <input
                  className="pc-input"
                  value={item.pergunta}
                  onChange={(e) => atualizarItem(idx, "pergunta", e.target.value)}
                />
              </div>

              <div className="pc-form-group">
                <label>Tipo de resposta</label>
                <select
                  className="pc-input"
                  value={item.tipoResposta}
                  onChange={(e) => atualizarItem(idx, "tipoResposta", e.target.value)}
                >
                  <option value="SIM_NAO">Sim / Não</option>
                  <option value="OK_NOK">OK / NOK</option>
                  <option value="BAR">Barra / Numérico</option>
                  <option value="TEXTO">Texto Livre</option>
                </select>
              </div>

              <div className="pc-form-group">
                <label>Foto de referencia (opcional)</label>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button
                    type="button"
                    className="pc-btn-secundario"
                    onClick={() => inputItemFotoCameraRefs.current[idx]?.click()}
                  >
                    📷 Camera
                  </button>
                  <button
                    type="button"
                    className="pc-btn-secundario"
                    onClick={() => inputItemFotoArquivoRefs.current[idx]?.click()}
                  >
                    ⬆️ Arquivo
                  </button>
                </div>
                <input
                  ref={(el) => (inputItemFotoCameraRefs.current[idx] = el)}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  style={{ display: "none" }}
                  onChange={(e) => atualizarFotoItem(idx, e.target.files?.[0] || null)}
                />
                <input
                  ref={(el) => (inputItemFotoArquivoRefs.current[idx] = el)}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={(e) => atualizarFotoItem(idx, e.target.files?.[0] || null)}
                />

                {item.fotoPreview || item.fotoUrl ? (
                  <div style={{ marginTop: 8 }}>
                    <img
                      src={item.fotoPreview || item.fotoUrl}
                      alt="Preview"
                      style={{ maxWidth: 240, borderRadius: 8 }}
                    />
                    <div>
                      <button type="button" className="pc-btn-secundario" onClick={() => removerFotoItem(idx)}>
                        Remover foto
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>

              <button type="button" className="pc-btn-secundario" onClick={() => removerItem(idx)}>
                Remover item
              </button>
            </div>
          ))}

          <button type="button" className="pc-btn pc-btn-primary" onClick={adicionarItem} style={{ marginTop: 12 }}>
            + Adicionar item
          </button>
        </div>

        <div style={{ marginTop: 24, display: "flex", gap: 10 }}>
          <button className="pc-btn pc-btn-primary" type="submit" disabled={salvando}>
            {salvando ? "Salvando..." : "Salvar alteracoes"}
          </button>
          <button className="pc-btn" type="button" onClick={() => navigate("/cadastros/checklists")}>Voltar</button>
        </div>
      </form>
    </>
  );

  if (carregando) {
    return (
      <LayoutDesktop tema={tema} alterarTema={alterarTema} titulo="Editar Checklist" abaBottom="cadastros">
        <p style={{ padding: 14 }}>Carregando...</p>
      </LayoutDesktop>
    );
  }

  const Layout = isMobile ? LayoutMobile : LayoutDesktop;

  return (
    <Layout
      tema={tema}
      alterarTema={alterarTema}
      titulo={isMobile ? "Editar Checklist" : "EDITAR CHECKLIST"}
      subtitulo={"Cadastros / Checklists / Editar"}
      abaBottom={isMobile ? "cadastros" : undefined}
    >
      {renderFormulario()}
    </Layout>
  );
}

export default EditarChecklist;
